import { useCallback, useEffect, useRef, useState } from "react";

/** 音频来源类型：便于未来扩展 BACKGROUND / VOICE_SAMPLE / AI_GENERATED */
export enum PodcastAudioSourceType {
  WORK = "WORK",
  TEMPLATE = "TEMPLATE",
  PREVIEW = "PREVIEW",
  DETAIL = "DETAIL",
}

export type PodcastAudioSource =
  | { type: PodcastAudioSourceType.WORK; id: string }
  | { type: PodcastAudioSourceType.TEMPLATE; id: string }
  | { type: PodcastAudioSourceType.PREVIEW; id: string }
  | { type: PodcastAudioSourceType.DETAIL; id: string };

export interface PodcastAudioPlayOptions {
  source: PodcastAudioSource;
  onStart?: () => void;
  onEnd?: () => void;
}

export interface PodcastAudioManager {
  current: PodcastAudioSource | null;
  playing: boolean;
  currentTime: number;
  duration: number;
  progress: number; // currentTime / duration, 0–1
  play: (url: string, opts: PodcastAudioPlayOptions) => Promise<void>;
  pause: () => void;
  toggle: (url: string, opts: PodcastAudioPlayOptions) => Promise<void>;
  stop: () => void;
  seek: (ratio: number) => void;
}

/**
 * 页面级单 Audio Owner：
 * 整个页面只维护一个 <audio>，切换来源即隐式互斥，杜绝多音频重叠播放。
 */
export function usePodcastAudioManager(): PodcastAudioManager {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [current, setCurrent] = useState<PodcastAudioSource | null>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // mount 一次性创建 Audio + 挂 listener；卸载时清理
  useEffect(() => {
    const audio = new Audio();
    audio.preload = "auto";
    const onTime = () => {
      setCurrentTime(audio.currentTime);
      if (audio.duration) setDuration(audio.duration);
    };
    const onEnd = () => {
      setPlaying(false);
      setCurrentTime(0);
    };
    const onError = () => {
      setPlaying(false);
    };
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onTime);
    audio.addEventListener("ended", onEnd);
    audio.addEventListener("error", onError);
    audioRef.current = audio;
    return () => {
      audio.pause();
      audio.src = "";
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onTime);
      audio.removeEventListener("ended", onEnd);
      audio.removeEventListener("error", onError);
      audioRef.current = null;
    };
  }, []);

  const play = useCallback(
    async (url: string, opts: PodcastAudioPlayOptions) => {
      const audio = audioRef.current;
      if (!audio || !url) return;
      // 隐式互斥：先暂停旧的
      audio.pause();
      audio.src = url;
      audio.currentTime = 0;
      opts.onStart?.();
      setCurrent(opts.source);
      setCurrentTime(0);
      setDuration(0);
      try {
        await audio.play();
        setPlaying(true);
      } catch {
        setPlaying(false);
        // 竞态保护：只有本次请求仍是最新时才清状态
        if (audio.src === url) {
          setCurrent(null);
          setCurrentTime(0);
        }
      }
    },
    []
  );

  const pause = useCallback(() => {
    audioRef.current?.pause();
    setPlaying(false);
  }, []);

  const stop = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    setCurrent(null);
    setPlaying(false);
    setCurrentTime(0);
    setDuration(0);
  }, []);

  const toggle = useCallback(
    async (url: string, opts: PodcastAudioPlayOptions) => {
      const audio = audioRef.current;
      if (!audio || !url) return;
      const same =
        current &&
        current.type === opts.source.type &&
        current.id === opts.source.id;
      if (same && !audio.paused) {
        pause();
        return;
      }
      await play(url, opts);
    },
    [current, play, pause]
  );

  const seek = useCallback((ratio: number) => {
    const audio = audioRef.current;
    if (!audio || !audio.duration) return;
    const clamped = Math.max(0, Math.min(1, ratio));
    audio.currentTime = clamped * audio.duration;
    setCurrentTime(audio.currentTime);
  }, []);

  const progress = duration > 0 ? currentTime / duration : 0;

  return { current, playing, currentTime, duration, progress, play, pause, toggle, stop, seek };
}